/**
 * Centrální stav aplikace: jednoduchý pub/sub store
 * + persistence (localStorage) + sdílení přes URL + import/export JSON.
 *
 * Citlivé parametry (vlastní endpointy, API klíče) zůstávají POUZE
 * v localStorage tohoto prohlížeče — sdílecí odkaz ani export je nenese.
 */
import { createCustomMode, defaultModes } from '../core/modes';
import { defaultSortOptions } from '../core/metrics';
import {
  isParamId,
  PARAM_DEFS,
  resolveParams,
  sanitizeOverrides,
  type ParamDef,
  type ParamOverrides,
  type ResolvedParams,
} from '../core/params';
import {
  defaultProfiles,
  findProfile,
  sanitizeProfiles,
  sortedProfiles,
  type Profile,
} from '../core/profiles';
import { sanitizePrograms, type CustomProgram } from '../core/programs';
import {
  defaultSearchProfiles,
  sanitizeSearchProfiles,
  type SearchProfile,
  type SearchStrategy,
} from '../core/searchProfiles';
import { msFromPragueLocal, pragueParts } from '../core/time';
import type {
  ModeProfile,
  NamedPoint,
  SortKey,
  SortOptions,
  TransitNetwork,
  ZoneMetric,
} from '../core/types';

export type TimeMode = 'now' | 'depart';
export type Theme = 'light' | 'dark';

/**
 * Zadání časového okna: vždy se interpretuje jako interval [from, to].
 * Režim 'range' = od–do (libovolná mez může chybět = bez omezení);
 * režim 'center' = „v ± tolerance". Prázdné vše = bez omezení.
 */
export interface TimeWindowInput {
  mode: 'range' | 'center';
  from: string | null;
  to: string | null;
  center: string | null;
  tolMin: number;
}

export function emptyWindow(tolMin = 0): TimeWindowInput {
  return { mode: 'center', from: null, to: null, center: null, tolMin };
}

/** Validace časového okna z cizího zdroje. */
export function sanitizeTimeWindow(raw: unknown, fallback: TimeWindowInput): TimeWindowInput {
  if (!raw || typeof raw !== 'object') return fallback;
  const w = raw as Partial<TimeWindowInput>;
  const valid = (v: unknown): string | null =>
    typeof v === 'string' && parseLocalToMs(v) !== null ? v : null;
  return {
    mode: w.mode === 'range' ? 'range' : 'center',
    from: valid(w.from),
    to: valid(w.to),
    center: valid(w.center),
    tolMin: Number.isFinite(w.tolMin) ? Math.max(0, Math.min(720, w.tolMin as number)) : fallback.tolMin,
  };
}

/** Efektivní meze okna v ms; null = bez omezení. */
export function windowBoundsMs(w: TimeWindowInput): { fromMs: number | null; toMs: number | null } {
  if (w.mode === 'center') {
    if (!w.center) return { fromMs: null, toMs: null };
    const c = parseLocalToMs(w.center);
    if (c === null) return { fromMs: null, toMs: null };
    const tol = Math.max(0, w.tolMin) * 60_000;
    return { fromMs: c - tol, toMs: c + tol };
  }
  return {
    fromMs: w.from ? parseLocalToMs(w.from) : null,
    toMs: w.to ? parseLocalToMs(w.to) : null,
  };
}

/** Převod zadání mezi režimy od–do ⇄ v± (při přepnutí přepínače). */
export function convertWindow(w: TimeWindowInput, target: 'range' | 'center'): TimeWindowInput {
  if (w.mode === target) return w;
  const { fromMs, toMs } = windowBoundsMs(w);
  const toLocal = (ms: number): string => {
    const p = pragueParts(ms);
    const pad = (n: number): string => String(n).padStart(2, '0');
    return `${p.year}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}`;
  };
  if (target === 'center') {
    if (fromMs !== null && toMs !== null) {
      return { mode: 'center', from: null, to: null, center: toLocal((fromMs + toMs) / 2), tolMin: Math.round((toMs - fromMs) / 120_000) };
    }
    const single = fromMs ?? toMs;
    return { mode: 'center', from: null, to: null, center: single !== null ? toLocal(single) : null, tolMin: 0 };
  }
  if (fromMs === null && toMs === null) return { mode: 'range', from: null, to: null, center: null, tolMin: w.tolMin };
  return { mode: 'range', from: fromMs !== null ? toLocal(fromMs) : null, to: toMs !== null ? toLocal(toMs) : null, center: null, tolMin: w.tolMin };
}

/** Zdroj jízdních řádů MHD. Demo je vždy výslovná volba, ne tichý default. */
export type TransitSource = 'none' | 'demo' | 'manual' | 'gtfs';

/** Metadata staženého GTFS feedu — řídí „stáhnout jednou × aktualizovat při změně". */
export interface GtfsMeta {
  /** Id z katalogu (pid, idsjmk…), null = vlastní URL/soubor. */
  sourceId: string | null;
  /** URL, ze které se stahovalo (proxy nebo vlastní); null = ruční soubor. */
  url: string | null;
  /** HTTP validátory pro podmíněné stažení. */
  etag: string | null;
  lastModified: string | null;
  /** Kdy proběhl import (ISO, Europe/Prague čas vzniku). */
  downloadedAt: string;
  /** Oblast, na kterou byl feed ořezán; null = celý feed. */
  bbox: { south: number; west: number; north: number; east: number } | null;
}

export interface AppState {
  origin: NamedPoint | null;
  destination: NamedPoint | null;
  /** Okno odjezdu a příjezdu (časy „YYYY-MM-DDTHH:mm" v Europe/Prague). */
  timeDep: TimeWindowInput;
  timeArr: TimeWindowInput;
  modes: ModeProfile[];
  /** Zdroj jízdních řádů; 'none' = uživatel zatím nevybral. */
  transitSource: TransitSource;
  /** Ručně zadaná síť MHD. */
  manualTransit: TransitNetwork | null;
  /** Síť importovaná z GTFS. */
  gtfsTransit: TransitNetwork | null;
  /** Název importovaného GTFS feedu (pro UI). */
  gtfsFeedName: string | null;
  /** Metadata stažení (validátory pro aktualizaci, oblast ořezu). */
  gtfsMeta: GtfsMeta | null;
  sort: SortOptions;
  zonesEnabled: boolean;
  zoneMetric: ZoneMetric;
  /** Režimy (id) vstupující do zón; null = všechny zapnuté. */
  zoneModeIds: string[] | null;
  selectedItineraryId: string | null;
  /** Zobrazit na mapě jen vybranou trasu. */
  soloRoute: boolean;
  theme: Theme;
  /** Globální přepisy parametrů (viz core/params.ts). */
  paramValues: ParamOverrides;
  /** Profily nastavení a aktivní profil. */
  profiles: Profile[];
  activeProfileId: string;
  /** Strategie hledání (nejrychleji tam / nejpozdější odjezd). */
  strategy: SearchStrategy;
  /** Profily hledání a aktivní profil. */
  searchProfiles: SearchProfile[];
  activeSearchProfile: string | null;
  /** Vlastní programy (řazení/filtry/hledání) a jejich aktivace. */
  customPrograms: CustomProgram[];
  customSortId: string | null;
  customSearchId: string | null;
  customFilterIds: string[];
  onboarded: boolean;
}

const STORAGE_KEY = 'optiroute-state-v1';
const SHARE_VERSION = 2;

export function nowDepartLocal(): string {
  const p = pragueParts(Date.now());
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${p.year}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}`;
}

export function defaultState(): AppState {
  return {
    origin: null,
    destination: null,
    timeDep: emptyWindow(0),
    timeArr: emptyWindow(15),
    modes: defaultModes(),
    transitSource: 'none',
    manualTransit: null,
    gtfsTransit: null,
    gtfsFeedName: null,
    gtfsMeta: null,
    sort: defaultSortOptions(),
    zonesEnabled: false,
    zoneMetric: 'bestMode',
    zoneModeIds: null,
    selectedItineraryId: null,
    soloRoute: false,
    theme: window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light',
    paramValues: {},
    profiles: defaultProfiles(),
    activeProfileId: 'p-default',
    strategy: 'default',
    searchProfiles: defaultSearchProfiles(),
    activeSearchProfile: 'sp-default',
    customPrograms: [],
    customSortId: null,
    customSearchId: null,
    customFilterIds: [],
    onboarded: false,
  };
}

/** Aplikuje profil hledání — jen sekce, které má uložené. */
export function applySearchProfileToState(state: AppState, id: string): Partial<AppState> {
  const p = state.searchProfiles.find((x) => x.id === id);
  if (!p) return {};
  const out: Partial<AppState> = { activeSearchProfile: id, selectedItineraryId: null };
  const d = p.data;
  if (d.strategy) out.strategy = d.strategy;
  if (d.sort) out.sort = { ...defaultSortOptions(), ...d.sort, weights: { ...defaultSortOptions().weights, ...d.sort.weights } };
  if (d.programs) {
    const valid = (x: string | null): string | null => (x && state.customPrograms.some((c) => c.id === x) ? x : null);
    out.customSortId = valid(d.programs.customSortId);
    out.customSearchId = valid(d.programs.customSearchId);
    out.customFilterIds = d.programs.customFilterIds.filter((x) => state.customPrograms.some((c) => c.id === x));
  }
  if (d.enabledModeIds) {
    const set = new Set(d.enabledModeIds);
    out.modes = state.modes.map((m) => ({ ...m, enabled: set.has(m.id) }));
  }
  if (d.timeDep) out.timeDep = sanitizeTimeWindow(d.timeDep, emptyWindow(0));
  if (d.timeArr) out.timeArr = sanitizeTimeWindow(d.timeArr, emptyWindow(15));
  return out;
}

/** Sebere ze současného stavu data pro zvolené sekce profilu hledání. */
export function captureSearchProfileData(
  state: AppState,
  sections: { strategy: boolean; sort: boolean; programs: boolean; modes: boolean; windows: boolean },
): SearchProfile['data'] {
  return {
    strategy: sections.strategy ? state.strategy : undefined,
    sort: sections.sort ? state.sort : undefined,
    programs: sections.programs
      ? { customSortId: state.customSortId, customSearchId: state.customSearchId, customFilterIds: [...state.customFilterIds] }
      : undefined,
    enabledModeIds: sections.modes ? state.modes.filter((m) => m.enabled).map((m) => m.id) : undefined,
    timeDep: sections.windows ? state.timeDep : undefined,
    timeArr: sections.windows ? state.timeArr : undefined,
  };
}

/** Efektivní parametry: výchozí < globální < aktivní profil. */
export function activeParams(state: AppState): ResolvedParams {
  return resolveParams(state.paramValues, findProfile(state.profiles, state.activeProfileId));
}

/**
 * Přepnutí profilu „nasadí" jeho hodnoty do sezení (řazení, váhy, filtry,
 * metrika zón). Ostatní parametry (špička, tarif…) se čtou živě přes
 * activeParams, takže se projeví okamžitě bez kopírování.
 */
export function applyProfileToSession(state: AppState, profileId: string): Partial<AppState> {
  const profile = findProfile(state.profiles, profileId);
  if (!profile) return {};
  const p = resolveParams(state.paramValues, profile);
  return {
    activeProfileId: profileId,
    sort: {
      key: p.get('sort.key') as SortKey,
      carFreeOnly: p.get('filter.carFree'),
      accessibleOnly: p.get('filter.accessible'),
      weights: {
        time: p.get('weights.time'),
        cost: p.get('weights.cost'),
        comfort: p.get('weights.comfort'),
        emissions: p.get('weights.emissions'),
      },
    },
    zoneMetric: p.get('zones.metric') as ZoneMetric,
    selectedItineraryId: null,
  };
}

/**
 * Referenční čas odjezdu v ms: spodek okna odjezdu; bez zadání = teď.
 * (Slouží plánovači jako výchozí bod, špičce a zónám jako simulovaný čas.)
 */
export function departMsOf(state: AppState): number {
  const dep = windowBoundsMs(state.timeDep);
  if (dep.fromMs !== null) return dep.fromMs;
  // Jen příjezdové okno → orientačně teď (plánovač dopočítá posun sám).
  return Date.now();
}

/** Okna pro plánovač (null = bez omezení). */
export function planWindowsOf(state: AppState): {
  depFromMs: number | null;
  depToMs: number | null;
  arrFromMs: number | null;
  arrToMs: number | null;
} {
  const d = windowBoundsMs(state.timeDep);
  const a = windowBoundsMs(state.timeArr);
  return { depFromMs: d.fromMs, depToMs: d.toMs, arrFromMs: a.fromMs, arrToMs: a.toMs };
}

export function parseLocalToMs(local: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(local);
  if (!m) return null;
  return msFromPragueLocal(Number(m[1]), Number(m[2]), Number(m[3]), Number(m[4]), Number(m[5]));
}

type Listener = (state: AppState) => void;

export class Store {
  private state: AppState;
  private listeners = new Set<Listener>();
  private saveTimer: number | undefined;

  constructor(initial: AppState) {
    this.state = initial;
  }

  get(): AppState {
    return this.state;
  }

  update(patch: Partial<AppState>): void {
    this.state = { ...this.state, ...patch };
    for (const l of this.listeners) l(this.state);
    this.scheduleSave();
  }

  /** Mutace režimu podle id (imutabilně). */
  updateMode(id: string, patch: Partial<ModeProfile>): void {
    this.update({
      modes: this.state.modes.map((m) => (m.id === id ? { ...m, ...patch } : m)),
    });
  }

  removeMode(id: string): void {
    this.update({ modes: this.state.modes.filter((m) => m.id !== id) });
  }

  subscribe(l: Listener): () => void {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  }

  private scheduleSave(): void {
    if (this.saveTimer !== undefined) window.clearTimeout(this.saveTimer);
    this.saveTimer = window.setTimeout(() => saveState(this.state), 400);
  }
}

/* ------------------------------------------------------------------ */
/* Persistence                                                          */
/* ------------------------------------------------------------------ */

/** Co se ukládá lokálně (bez UI efemér jako vybraná trasa). */
function persistable(state: AppState): Record<string, unknown> {
  return {
    v: SHARE_VERSION,
    origin: state.origin,
    destination: state.destination,
    timeDep: state.timeDep,
    timeArr: state.timeArr,
    modes: state.modes,
    transitSource: state.transitSource,
    manualTransit: state.manualTransit,
    gtfsTransit: state.gtfsTransit,
    gtfsFeedName: state.gtfsFeedName,
    gtfsMeta: state.gtfsMeta,
    sort: state.sort,
    zonesEnabled: state.zonesEnabled,
    zoneMetric: state.zoneMetric,
    zoneModeIds: state.zoneModeIds,
    theme: state.theme,
    paramValues: state.paramValues,
    profiles: state.profiles,
    activeProfileId: state.activeProfileId,
    strategy: state.strategy,
    searchProfiles: state.searchProfiles,
    activeSearchProfile: state.activeSearchProfile,
    customPrograms: state.customPrograms,
    customSortId: state.customSortId,
    customSearchId: state.customSearchId,
    customFilterIds: state.customFilterIds,
    onboarded: state.onboarded,
  };
}

/** Citlivé parametry (endpointy, klíče) — nikdy neopouštějí prohlížeč. */
function stripSensitive(values: ParamOverrides): ParamOverrides {
  const out: ParamOverrides = {};
  for (const [k, v] of Object.entries(values)) {
    if (!isParamId(k)) continue;
    if ((PARAM_DEFS[k] as ParamDef).sensitive) continue;
    out[k] = v as ParamOverrides[typeof k];
  }
  return out;
}

/**
 * Co se sdílí odkazem a exportuje do souboru: bez citlivých parametrů.
 * GTFS síť se navíc nevkládá do URL (limit délky) — jen do exportu.
 */
function sharablePayload(state: AppState, opts?: { includeGtfs?: boolean }): Record<string, unknown> {
  const out = persistable(state);
  out.paramValues = stripSensitive(state.paramValues);
  if (!opts?.includeGtfs) {
    delete out.gtfsTransit;
    delete out.gtfsFeedName;
    delete out.gtfsMeta;
    if (state.transitSource === 'gtfs') out.transitSource = 'none';
  }
  return out;
}

function saveState(state: AppState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(persistable(state)));
  } catch {
    // plné/nedostupné úložiště nesmí nic rozbít
  }
}

/**
 * Bezpečné sloučení uloženého/sdíleného payloadu do výchozího stavu.
 * trusted=true jen pro localStorage — citlivé parametry se z netrusted
 * zdrojů (sdílecí odkaz, import souboru) zahazují.
 */
export function applyPayload(base: AppState, raw: unknown, opts?: { trusted?: boolean }): AppState {
  if (typeof raw !== 'object' || raw === null) return base;
  const p = raw as Record<string, unknown>;
  const out = { ...base };

  const isPoint = (x: unknown): x is NamedPoint =>
    typeof x === 'object' &&
    x !== null &&
    Number.isFinite((x as NamedPoint).lat) &&
    Number.isFinite((x as NamedPoint).lng);

  if (isPoint(p.origin)) out.origin = p.origin;
  if (isPoint(p.destination)) out.destination = p.destination;

  // Časová okna: validace; legacy formát (timeMode/departLocal/arriveByLocal) migrujeme.
  const validLocal = (v: unknown): string | null =>
    typeof v === 'string' && parseLocalToMs(v) !== null ? v : null;
  if (p.timeDep || p.timeArr) {
    out.timeDep = sanitizeTimeWindow(p.timeDep, emptyWindow(0));
    out.timeArr = sanitizeTimeWindow(p.timeArr, emptyWindow(15));
  } else if (p.timeMode === 'depart' || p.arriveByLocal) {
    out.timeDep =
      p.timeMode === 'depart' && validLocal(p.departLocal)
        ? { mode: 'center', from: null, to: null, center: p.departLocal as string, tolMin: 0 }
        : emptyWindow(0);
    out.timeArr = validLocal(p.arriveByLocal)
      ? { mode: 'range', from: null, to: p.arriveByLocal as string, center: null, tolMin: 15 }
      : emptyWindow(15);
  }
  if (Array.isArray(p.modes) && p.modes.length > 0) {
    // Sloučíme: známé režimy aktualizujeme, vlastní převezmeme.
    const incoming = p.modes.filter(
      (m): m is ModeProfile =>
        typeof m === 'object' && m !== null && typeof (m as ModeProfile).id === 'string',
    );
    // Číselné parametry z cizího payloadu mohou být cokoli — nahradíme nesmysly výchozími.
    const NUM_KEYS = [
      'speedKmh', 'circuity', 'setupMin', 'teardownMin', 'waitMin',
      'costFixed', 'costPerKm', 'emissionsPerKm', 'comfort', 'reliability',
    ] as const;
    const sanitizeMode = (m: ModeProfile, fallback: ModeProfile): ModeProfile => {
      const s = { ...m };
      for (const k of NUM_KEYS) {
        if (!Number.isFinite(s[k])) s[k] = fallback[k];
      }
      if (typeof s.name !== 'string' || s.name.length === 0) s.name = fallback.name;
      if (typeof s.icon !== 'string') s.icon = fallback.icon;
      if (typeof s.color !== 'string') s.color = fallback.color;
      s.enabled = s.enabled === true;
      s.accessible = s.accessible === true;
      s.carFree = s.carFree !== false;
      return s;
    };
    const defaults = defaultModes();
    const customTemplate = createCustomMode();
    const merged: ModeProfile[] = defaults.map((d) => {
      const found = incoming.find((m) => m.id === d.id);
      return found ? sanitizeMode({ ...d, ...found }, d) : d;
    });
    for (const m of incoming) {
      if (!merged.some((x) => x.id === m.id)) {
        merged.push(sanitizeMode({ ...customTemplate, ...m, userDefined: true }, customTemplate));
      }
    }
    out.modes = merged;
  }
  // Prvky polí mohou být cokoli (poškozený/zlomyslný payload) — validujeme každý zvlášť.
  const sanitizeNetwork = (
    raw2: unknown,
    source: TransitNetwork['source'],
  ): TransitNetwork | null => {
    if (!raw2 || typeof raw2 !== 'object') return null;
    const t = raw2 as TransitNetwork;
    const isStop = (s: unknown): s is TransitNetwork['stops'][number] =>
      typeof s === 'object' &&
      s !== null &&
      Number.isFinite((s as { lat?: unknown }).lat) &&
      Number.isFinite((s as { lng?: unknown }).lng);
    const isLine = (l: unknown): l is TransitNetwork['lines'][number] =>
      typeof l === 'object' &&
      l !== null &&
      Array.isArray((l as { stopIds?: unknown }).stopIds) &&
      ((l as { stopIds: unknown[] }).stopIds as unknown[]).every((x) => typeof x === 'string');
    if (!Array.isArray(t.stops) || !Array.isArray(t.lines)) return null;
    const stops = t.stops.filter(isStop);
    const lines = t.lines.filter(isLine);
    // Síť bez zastávek nebo linek je degenerovaná — zahazujeme.
    if (stops.length === 0 || lines.length === 0) return null;
    return {
      stops,
      lines,
      maxAccessKm: Number.isFinite(t.maxAccessKm) ? t.maxAccessKm : 1.2,
      transferMin: Number.isFinite(t.transferMin) ? t.transferMin : 3,
      source,
    };
  };
  const manual = sanitizeNetwork(p.manualTransit, 'manual');
  if (manual) out.manualTransit = manual;
  const gtfs = sanitizeNetwork(p.gtfsTransit, 'gtfs');
  if (gtfs) out.gtfsTransit = gtfs;
  if (typeof p.gtfsFeedName === 'string') out.gtfsFeedName = p.gtfsFeedName.slice(0, 120);
  if (p.gtfsMeta && typeof p.gtfsMeta === 'object') {
    const m = p.gtfsMeta as Partial<GtfsMeta>;
    const bbox =
      m.bbox &&
      typeof m.bbox === 'object' &&
      Number.isFinite(m.bbox.south) &&
      Number.isFinite(m.bbox.west) &&
      Number.isFinite(m.bbox.north) &&
      Number.isFinite(m.bbox.east)
        ? { south: m.bbox.south, west: m.bbox.west, north: m.bbox.north, east: m.bbox.east }
        : null;
    out.gtfsMeta = {
      sourceId: typeof m.sourceId === 'string' ? m.sourceId : null,
      url: typeof m.url === 'string' ? m.url.slice(0, 500) : null,
      etag: typeof m.etag === 'string' ? m.etag.slice(0, 200) : null,
      lastModified: typeof m.lastModified === 'string' ? m.lastModified.slice(0, 100) : null,
      downloadedAt: typeof m.downloadedAt === 'string' ? m.downloadedAt.slice(0, 40) : '',
      bbox,
    };
  }

  // Zdroj MHD: explicitní hodnota, jinak migrace ze starého formátu (v1):
  // ruční síť → manual; stará data bez volby → demo (tak se aplikace chovala).
  const SOURCES: TransitSource[] = ['none', 'demo', 'manual', 'gtfs'];
  if (typeof p.transitSource === 'string' && SOURCES.includes(p.transitSource as TransitSource)) {
    out.transitSource = p.transitSource as TransitSource;
  } else if (p.v === 1 || p.settings) {
    out.transitSource = out.manualTransit ? 'manual' : 'demo';
  }
  // Konzistence: zdroj bez dat se vrací na 'none'.
  if (out.transitSource === 'manual' && !out.manualTransit) out.transitSource = 'none';
  if (out.transitSource === 'gtfs' && !out.gtfsTransit) out.transitSource = 'none';
  if (p.sort && typeof p.sort === 'object') {
    const s = p.sort as Partial<SortOptions>;
    const d = defaultSortOptions();
    const SORT_KEYS = ['time', 'arrival', 'departure', 'cost', 'wait', 'emissions', 'distance', 'comfort', 'transfers', 'score'];
    const w = (s.weights ?? {}) as Record<string, unknown>;
    const weight = (k: keyof SortOptions['weights']): number =>
      Number.isFinite(w[k]) ? Math.min(1, Math.max(0, w[k] as number)) : d.weights[k];
    out.sort = {
      key: typeof s.key === 'string' && SORT_KEYS.includes(s.key) ? s.key : d.key,
      carFreeOnly: s.carFreeOnly === true,
      accessibleOnly: s.accessibleOnly === true,
      weights: { time: weight('time'), cost: weight('cost'), comfort: weight('comfort'), emissions: weight('emissions') },
    };
  }
  if (typeof p.zonesEnabled === 'boolean') out.zonesEnabled = p.zonesEnabled;
  const ZONE_METRICS: ZoneMetric[] = [
    'bestMode', 'weighted', 'time', 'wait', 'cost', 'emissions', 'effort', 'comfort', 'transfers', 'uncertainty',
  ];
  if (typeof p.zoneMetric === 'string' && ZONE_METRICS.includes(p.zoneMetric as ZoneMetric)) {
    out.zoneMetric = p.zoneMetric as ZoneMetric;
  }
  if (Array.isArray(p.zoneModeIds)) {
    out.zoneModeIds = p.zoneModeIds.filter((x): x is string => typeof x === 'string').slice(0, 50);
  } else if (p.zoneModeIds === null) {
    out.zoneModeIds = null;
  }
  if (p.theme === 'light' || p.theme === 'dark') out.theme = p.theme;

  // Parametry: validace přes registr; z netrusted zdrojů bez citlivých hodnot.
  if (p.paramValues && typeof p.paramValues === 'object') {
    const clean = sanitizeOverrides(p.paramValues);
    const merged = { ...base.paramValues, ...(opts?.trusted ? clean : stripSensitive(clean)) };
    out.paramValues = merged;
  }
  // Migrace starého formátu `settings` (v1) na parametry.
  if (p.settings && typeof p.settings === 'object') {
    const s = p.settings as Record<string, unknown>;
    const pv = { ...out.paramValues };
    if (typeof s.onlineGeocode === 'boolean') pv['geocode.online'] = s.onlineGeocode;
    if (typeof s.onlineRouting === 'boolean') pv['routing.online'] = s.onlineRouting;
    if (opts?.trusted) {
      if (typeof s.nominatimEndpoint === 'string' && s.nominatimEndpoint)
        pv['geocode.nominatimEndpoint'] = s.nominatimEndpoint;
      if (typeof s.osrmEndpoint === 'string' && s.osrmEndpoint)
        pv['routing.osrmEndpoint'] = s.osrmEndpoint;
    }
    out.paramValues = pv;
  }

  // Profily: validace + aktivní profil musí existovat.
  const profiles = sanitizeProfiles(p.profiles);
  if (profiles) out.profiles = sortedProfiles(profiles).map((pr, i) => ({ ...pr, order: i }));
  if (typeof p.activeProfileId === 'string' && out.profiles.some((x) => x.id === p.activeProfileId)) {
    out.activeProfileId = p.activeProfileId;
  } else if (!out.profiles.some((x) => x.id === out.activeProfileId)) {
    out.activeProfileId = out.profiles[0]?.id ?? 'p-default';
  }

  // Strategie a profily hledání.
  if (p.strategy === 'default' || p.strategy === 'earliest' || p.strategy === 'latest') {
    out.strategy = p.strategy;
  }
  const sps = sanitizeSearchProfiles(p.searchProfiles);
  if (sps) out.searchProfiles = sps;
  if (typeof p.activeSearchProfile === 'string' && out.searchProfiles.some((x) => x.id === p.activeSearchProfile)) {
    out.activeSearchProfile = p.activeSearchProfile;
  } else if (out.activeSearchProfile && !out.searchProfiles.some((x) => x.id === out.activeSearchProfile)) {
    out.activeSearchProfile = out.searchProfiles[0]?.id ?? null;
  }

  // Vlastní programy: z nedůvěryhodných zdrojů (odkaz, import) se surový JS
  // režim zahazuje — spustitelný kód nesmí přijít zvenčí bez vědomí uživatele.
  if (Array.isArray(p.customPrograms)) {
    out.customPrograms = sanitizePrograms(p.customPrograms, opts?.trusted === true);
  }
  const progId = (v: unknown): string | null =>
    typeof v === 'string' && out.customPrograms.some((x) => x.id === v) ? v : null;
  out.customSortId = progId(p.customSortId);
  out.customSearchId = progId(p.customSearchId);
  out.customFilterIds = Array.isArray(p.customFilterIds)
    ? p.customFilterIds.filter((x): x is string => progId(x) !== null)
    : [];

  if (typeof p.onboarded === 'boolean') out.onboarded = p.onboarded;
  return out;
}

export function loadState(): AppState {
  const base = defaultState();
  let state = base;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) state = applyPayload(base, JSON.parse(raw), { trusted: true });
  } catch {
    state = base;
  }
  // Sdílený stav v URL má přednost před uloženým. Poškozený či zlomyslný
  // odkaz nesmí shodit start aplikace — v nejhorším se prostě ignoruje.
  try {
    const shared = readShareUrl();
    if (shared) state = applyPayload(state, shared);
  } catch {
    // necháme stav z localStorage / výchozí
  }
  return state;
}

/* ------------------------------------------------------------------ */
/* Sdílení přes URL a import/export                                     */
/* ------------------------------------------------------------------ */

/** Base64url kódování UTF-8 JSON (bez závislostí). */
function encodePayload(obj: unknown): string {
  const json = JSON.stringify(obj);
  const bytes = new TextEncoder().encode(json);
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function decodePayload(s: string): unknown | null {
  try {
    const b64 = s.replace(/-/g, '+').replace(/_/g, '/');
    const bin = atob(b64 + '='.repeat((4 - (b64.length % 4)) % 4));
    const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes));
  } catch {
    return null;
  }
}

export function buildShareUrl(state: AppState): string {
  const url = new URL(window.location.href);
  url.hash = `s=${encodePayload(sharablePayload(state))}`;
  return url.toString();
}

export function readShareUrl(): unknown | null {
  const h = window.location.hash;
  const m = /^#s=(.+)$/.exec(h);
  if (!m) return null;
  return decodePayload(m[1]);
}

export function exportJson(state: AppState): string {
  return JSON.stringify(sharablePayload(state, { includeGtfs: true }), null, 2);
}

export function importJson(base: AppState, json: string): AppState | null {
  try {
    return applyPayload(base, JSON.parse(json));
  } catch {
    return null;
  }
}
